import { AccidentReport } from "@/models/accidentReport";
import { EvCarNoopEventsEditRequest, NoopReasonCode, RestartEstimate, SearchEvCarNoopEventsCondition } from "@/models/carUnderuseEvents";
import { Office } from "@/models/office";
import { getShortDatetime } from "@/util/datetime";

// 事故発生有無の定数
export const ACCIDENT_OCCURRENCE_KEYS = {
  ACCIDENT: 'accident',
  NO_ACCIDENT: 'no_accident',
} as const;

// 事故報告関連の定数
export const ACCIDENT_FEATURE_KEYS = {
  NOT_IN_LIST: 'not_in_list',
  NOT_REPORTED: 'not_reported',
  NON_ACCIDENT_FAILURE: 'non_accident_failure',
  OTHER: 'other',
} as const;

// 稼働再開見込みの定数
export const RESTART_DATE_TYPE_KEYS = {
  ONE_WEEK: '1w',
  TWO_WEEKS: '2w',
  UNKNOWN: 'unknown',
  NOT_NOOP: 'not_noop',
} as const;

export const CarNoopEventsConstants = {
  CarUnderuseEvents: "日々の稼働確認（事故発生確認 等）",
  ReasonReplyHistory: "理由登録履歴へ",
  CompanyOffice: "会社/営業所",
  Company: "会社",
  Office: "営業所",
  RadioNumber: "無線番号",
  PlateNumber: "ナンバープレート",
  NoopStartDate: "未稼働初日",
  EventAt: "検出日時",
  AccidentOccurrence: "事故発生有無/未稼働理由",
  AccidentOccurrenceShort: "事故発生有無",
  SelectRelatedAccident: "該当する事故を選択してください",
  SelectAccidentReportSubmission: "事故報告提出の有無を選択してください",
  AccidentTypeSelection: "区分選択",
  UnderuseReasonSelection: "未稼働理由を選択してください",
  UnderuseReason: "未稼働理由",
  RestartPlanDate: "稼働再開見込み",
  Date: "日付",
  Edit: "日々の稼働確認（事故発生確認 等）登録",
  Confirm: "事実確認",
  AskSubmitAccidentForm: "事故報告フォーム提出依頼",
  AppreciateDialogTitle: "ご回答ありがとうございます",
} as const;

export type CarNoopEventsEditState = {
  accidentOccurrence?: string; // ACCIDENT_OCCURRENCE_KEYS.ACCIDENT | ACCIDENT_OCCURRENCE_KEYS.NO_ACCIDENT
  selectedAccidentFeature?: number | string; // ACCIDENT_FEATURE_KEYS または事故ID
  underuseReason?: NoopReasonCode;
  restartPlanDateKey?: string;
  accidentTypeKey: string;
  accidentReportId?: number;
  falseDetection?: boolean;
}

export type CarNoopEventsEditValidationError = {
  accidentOccurrence?: boolean;
  selectedAccidentFeature?: boolean;
  restartPlanDateKey?: boolean;
}

export const INITIAL_EDIT_STATE: CarNoopEventsEditState = {
  accidentTypeKey: 'type1',
}

export type SavedState = {
  officesSelected: Office[];
  page: number;
  searchCondition: SearchEvCarNoopEventsCondition;
};

export const PAGE_SIZE = 30;

export const ACCIDENT_OCCURRENCE = {
  ACCIDENT: "事故",
  NO_ACCIDENT: "今回の未稼働車両について、事故報告漏れはない",
};

export const ACCIDENT_OCCURRENCE_OPTIONS = [
  {
    key: ACCIDENT_OCCURRENCE_KEYS.ACCIDENT,
    value: ACCIDENT_OCCURRENCE.ACCIDENT,
    note: "※以前事故が発生して、今回修理に出した場合等も「事故」をお選びください。"
  },
  {
    key: ACCIDENT_OCCURRENCE_KEYS.NO_ACCIDENT,
    value: ACCIDENT_OCCURRENCE.NO_ACCIDENT,
    note: "※事故が発生していない場合、こちらをお選びください。"
  },
];

export const ACCIDENT_REPORT_OPTIONS = {
  NOT_IN_LIST: "事故報告フォームを提出したが上記にない",
  NOT_REPORTED: "事故報告フォーム未提出",
  REPORTED: "事故報告フォーム提出済み",
};

export const NO_ACCIDENT_OPTIONS = [
  { key: ACCIDENT_FEATURE_KEYS.NON_ACCIDENT_FAILURE, value: "事故ではない故障" },
  { key: ACCIDENT_FEATURE_KEYS.OTHER, value: "事故や故障以外" },
];

// 4営未稼働理由（API に送る enum 文字列）
export const NOOP_REASONS = {
  UNREPORTED_ACCIDENT: "AccidentReportNotSubmitted",
  REPORTED_ACCIDENT: "AccidentReportSubmitted",
  NON_ACCIDENT_FAILURE: "Failure",
  OTHER: "Others",
} as const;

export const ACCIDENT_TYPE = [
  { key: "type1", value: "区分1" },
  { key: "type2", value: "区分2" },
];

export const RESTART_PLAN = [
  { key: RESTART_DATE_TYPE_KEYS.ONE_WEEK, value: "1週間以内程度" },
  { key: RESTART_DATE_TYPE_KEYS.TWO_WEEKS, value: "2週間以内程度" },
  { key: RESTART_DATE_TYPE_KEYS.UNKNOWN, value: "2週間以上/不明" },
  { key: RESTART_DATE_TYPE_KEYS.NOT_NOOP, value: "該当期間稼働しており、未稼働ではない" },
]

export type RestartDateType = typeof RESTART_DATE_TYPE_KEYS[keyof typeof RESTART_DATE_TYPE_KEYS];

export const ACCIDENT_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSed7zAmv8ivULrocE-0lAjRKIRhPokWZllNLtniVl9a6CxhYQ/viewform";
export const CRITICAL_ACCIDENT_URL = "https://wwwtb.mlit.go.jp/kyushu/content/000297513.pdf";

export const resolveEditRequest = (editState: CarNoopEventsEditState): EvCarNoopEventsEditRequest => {
  let restartEstimate: RestartEstimate | undefined = undefined;
  switch (editState.restartPlanDateKey) {
    case RESTART_DATE_TYPE_KEYS.ONE_WEEK:
      restartEstimate = "OneWeek";
      break;
    case RESTART_DATE_TYPE_KEYS.TWO_WEEKS:
      restartEstimate = "TwoWeeks";
      break;
  }

  return {
    underuseReason: editState.underuseReason ?? '',
    restartEstimate: restartEstimate,
    falseDetection: editState.restartPlanDateKey === RESTART_DATE_TYPE_KEYS.NOT_NOOP,
    accidentType: ACCIDENT_TYPE.find(a => a.key === editState.accidentTypeKey)?.value ?? '',
    accidentReportId: editState.accidentReportId,
  }
}

// 事故報告選択のタイトルメッセージを取得
export const getAccidentSelectionMessage = (accidentReports?: AccidentReport[]): string => {
  return accidentReports && accidentReports.length === 0
    ? CarNoopEventsConstants.SelectAccidentReportSubmission
    : CarNoopEventsConstants.SelectRelatedAccident;
};

// 事故選択肢のラベルを解決する（NOT_IN_LISTの場合）
const resolveNotInListLabel = (accidentReports?: AccidentReport[]): string => {
  // accidentReportsが空の場合は「事故報告フォーム提出済み」として表示
  return accidentReports && accidentReports.length === 0
    ? ACCIDENT_REPORT_OPTIONS.REPORTED
    : ACCIDENT_REPORT_OPTIONS.NOT_IN_LIST;
};

// 事故選択肢のラベルを解決する（NOT_REPORTEDの場合）
const resolveNotReportedLabel = (accidentType: string): string => {
  return `${ACCIDENT_REPORT_OPTIONS.NOT_REPORTED} ${accidentType}`;
};

// 事故選択肢のラベルを解決する（事故IDが選択された場合）
const resolveAccidentIdLabel = (editState: CarNoopEventsEditState, accidentReports?: AccidentReport[]): string => {
  const selectedReport = accidentReports?.find(r => String(r.id) === String(editState.selectedAccidentFeature));
  if (selectedReport) {
    const formatted = getShortDatetime(selectedReport.happenedAt);
    return `発生日時（${formatted}）`;
  } else {
    return `事故発生`;
  }
};

// 事故発生有無が「事故」の場合の選択内容ラベルを解決
const resolveAccidentSelectionLabel = (editState: CarNoopEventsEditState, accidentType: string, accidentReports?: AccidentReport[]): string => {
  switch (editState.selectedAccidentFeature) {
    case ACCIDENT_FEATURE_KEYS.NOT_IN_LIST:
      return resolveNotInListLabel(accidentReports);
    case ACCIDENT_FEATURE_KEYS.NOT_REPORTED:
      return resolveNotReportedLabel(accidentType);
    default:
      // 事故IDが選択された
      return resolveAccidentIdLabel(editState, accidentReports);
  }
};

// 事故発生有無が「事故報告漏れはない」の場合の選択内容ラベルを解決
const resolveNoAccidentSelectionLabel = (editState: CarNoopEventsEditState): string => {
  return NO_ACCIDENT_OPTIONS.find(o => o.key === editState.selectedAccidentFeature)?.value ?? '';
};

export const getEditWarnMessage = (editState: CarNoopEventsEditState, accidentReports?: any[]) => {
  const restartPlanMessage =
    `${CarNoopEventsConstants.RestartPlanDate}：${RESTART_PLAN.find(r => r.key === editState.restartPlanDateKey)?.value ?? ''}`;

  if (editState.falseDetection) {
    return restartPlanMessage;
  }

  const accidentType = ACCIDENT_TYPE.find(a => a.key === editState.accidentTypeKey)?.value ?? '';
  const accidentOccurrenceLabel = ACCIDENT_OCCURRENCE_OPTIONS.find(o => o.key === editState.accidentOccurrence)?.value ?? '';

  let selectionHeader = '';
  let selectionLabel = '';

  switch (editState.accidentOccurrence) {
    case ACCIDENT_OCCURRENCE_KEYS.ACCIDENT:
      selectionHeader = '選択内容';
      selectionLabel = resolveAccidentSelectionLabel(editState, accidentType, accidentReports);
      break;
    case ACCIDENT_OCCURRENCE_KEYS.NO_ACCIDENT:
      selectionHeader = '未稼働理由';
      selectionLabel = resolveNoAccidentSelectionLabel(editState);
      break;
  }

  return `${CarNoopEventsConstants.AccidentOccurrenceShort}：${accidentOccurrenceLabel}
${selectionHeader}：${selectionLabel}
` + restartPlanMessage;
}


export type AfterSubmitDialogInfo = {
  title: string;
  message: string;
  isInfoDialog: boolean;
};

export const resolveAfterSubmitDialogInfo = (underuseReason?: NoopReasonCode): AfterSubmitDialogInfo | undefined => {
  switch (underuseReason) {
    case NOOP_REASONS.UNREPORTED_ACCIDENT:
      return {
        title: CarNoopEventsConstants.AskSubmitAccidentForm,
        message: `早急にフォームのご提出をお願いします。
OKボタンを押すと、フォーム画面を開きます。`,
        isInfoDialog: false,
      }
    case NOOP_REASONS.REPORTED_ACCIDENT:
      return {
        title: CarNoopEventsConstants.AppreciateDialogTitle,
        message: `事故報告済みにも関わらず、お手間を取らせてしまい申し訳ございません。
対象抽出の精度を向上してまいりますので、引き続きご協力くださいますようお願いいたします。`,
        isInfoDialog: true,
      }
    case NOOP_REASONS.NON_ACCIDENT_FAILURE:
      return {
        title: CarNoopEventsConstants.AppreciateDialogTitle,
        message: `一日も早い修理完了を願っております。
事故報告の漏れ防止対策の一環として、引き続きご協力くださいますようお願いいたします。`,
        isInfoDialog: true,
      }
    case NOOP_REASONS.OTHER:
      return {
        title: CarNoopEventsConstants.AppreciateDialogTitle,
        message: `事故報告の漏れ防止対策の一環として、引き続きご協力くださいますようお願いいたします。`,
        isInfoDialog: true,
      }
    default:
      return undefined;
  }
}
